
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { usePremiumFeatures } from "@/hooks/use-premium-features";
import { Tool } from "@/hooks/use-canvas-setup";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { InfoIcon } from "lucide-react";

interface ToolbarDescriptionProps {
  toolDescription: string | undefined;
  isDrawingTool: boolean;
  rawMode?: boolean;
  onRawModeChange?: (enabled: boolean) => void;
}

export const ToolbarDescription = ({
  toolDescription,
  isDrawingTool,
  rawMode,
  onRawModeChange,
}: ToolbarDescriptionProps) => {
  const { isAuthenticated, handlePremiumFeature } = usePremiumFeatures();

  const handleRawModeChange = (enabled: boolean) => {
    if (!isAuthenticated && enabled) {
      handlePremiumFeature('precision_mode');
      return;
    }
    onRawModeChange?.(!enabled);
  };

  if (!toolDescription && !isDrawingTool) return null;

  // Split tooltip content into multiple lines
  const tooltipContent = rawMode
    ? "Precision Mode OFF:\nDrawing is smoothed\nand stabilized"
    : "Precision Mode ON:\nDrawing follows your\ninput precisely";

  return (
    <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex items-center justify-center">
      <div className="px-2.5 py-1 bg-[#F2FCE2]/90 backdrop-blur-sm text-[10px] rounded-full whitespace-nowrap border border-[#D3E4FD]/50 shadow-sm flex items-center gap-3">
        <span className="text-[#2A8D7B]">{toolDescription}</span>
        {isDrawingTool && (
          <>
            <div className="w-[1px] h-3 bg-[#D3E4FD]" />
            <div className="flex items-center gap-1.5">
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5">
                      <Switch
                        checked={!rawMode}
                        onCheckedChange={handleRawModeChange}
                        className={cn(
                          "h-[14px] w-[26px]",
                          "data-[state=checked]:bg-[#2A8D7B]/90",
                          "data-[state=unchecked]:bg-[#2A8D7B]/20",
                          "transition-colors duration-200"
                        )}
                        id="precise-mode"
                      />
                      <label 
                        htmlFor="precise-mode" 
                        className="text-[10px] text-[#2A8D7B] font-medium cursor-pointer select-none flex items-center gap-0.5"
                      >
                        Precision Mode
                        <InfoIcon className="h-3 w-3 text-[#2A8D7B]/70 cursor-help ml-0.5" />
                      </label>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent 
                    side="bottom" 
                    className="text-xs max-w-[200px] bg-white/95 backdrop-blur-sm text-gray-700 border border-[#D3E4FD] whitespace-pre-line rounded-lg"
                  >
                    {tooltipContent}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const getToolDescription = (tool: Tool): string => {
  switch (tool) {
    case "draw":
      return "Skin Marker";
    case "incision":
      return "Scalpel";
    case "scalpel":
      return "Scalpel";
    case "transform":
      return "Select";
    case "ruler":
      return "Measure";
    case "angle":
      return "Angles";
    case "shapes":
      return "Shapes";
    default:
      return "";
  }
};
